import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      lines: ['14346 Avenue 384', 'Yettem, CA 93292'],
    },
    {
      icon: Phone,
      title: 'Call Us',
      lines: ['(509) 405-7493'],
      link: 'tel:5094057493',
    },
    {
      icon: Mail,
      title: 'Email Us',
      lines: ['shahsmarket1@gmail.com'],
      link: 'mailto:shahsmarket1@gmail.com',
    },
    {
      icon: Clock,
      title: 'Hours',
      lines: ['Open Daily: 6AM-10PM'],
    },
  ];

  return (
    <div className="grain-bg" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#1A4D2E]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-fade-in-up">
              Contact Us
            </h1>
            <p className="text-xl text-white/80 animate-fade-in-up animation-delay-100">
              Have a question or feedback? We'd love to hear from you. Reach out anytime!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Contact Info Cards */}
            <div className="lg:col-span-1 space-y-4">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-5 shadow-lg card-hover"
                  data-testid={`contact-info-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#F4F1DE] rounded-xl flex items-center justify-center flex-shrink-0">
                      <info.icon size={24} className="text-[#1A4D2E]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#3D405B] mb-1">{info.title}</h3>
                      {info.lines.map((line, i) => (
                        info.link ? (
                          <a
                            key={i}
                            href={info.link}
                            className="block text-[#E07A5F] hover:underline"
                          >
                            {line}
                          </a>
                        ) : (
                          <p key={i} className="text-[#5F6C7B]">{line}</p>
                        )
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-[#3D405B] mb-2">Send Us a Message</h2>
                <p className="text-[#5F6C7B] mb-6">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>

                {submitStatus === 'success' && (
                  <div className="bg-[#81B29A]/20 border border-[#81B29A] rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="success-message">
                    <CheckCircle size={24} className="text-[#1A4D2E]" />
                    <div>
                      <p className="font-semibold text-[#1A4D2E]">Message Sent!</p>
                      <p className="text-sm text-[#3D405B]">Thank you for reaching out. We'll get back to you soon.</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-[#E07A5F]/20 border border-[#E07A5F] rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="error-message">
                    <AlertCircle size={24} className="text-[#E07A5F]" />
                    <div>
                      <p className="font-semibold text-[#E07A5F]">Something went wrong</p>
                      <p className="text-sm text-[#3D405B]">Please try again or call us directly.</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="contact-form space-y-5" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-[#3D405B] mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        className="w-full"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-[#3D405B] mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        className="w-full"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-[#3D405B] mb-2">
                      Phone Number (Optional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(559) 555-0123"
                      className="w-full"
                      data-testid="input-phone"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-[#3D405B] mb-2">
                      Your Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="How can we help you?"
                      className="w-full resize-none"
                      data-testid="input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 text-lg disabled:opacity-70 disabled:cursor-not-allowed"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="contact-map">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3D405B]">Find Us</h2>
            <p className="text-[#5F6C7B] mt-2">Conveniently located in Yettem, CA</p>
          </div>
          <div className="map-container h-[400px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3189.5!2d-119.1689!3d36.4689!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDI4JzA4LjAiTiAxMTnCsDEwJzA4LjAiVw!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Shah Market Location Map"
              data-testid="contact-google-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
